import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ProductoService } from '../../core/services/producto.service';
import { AlertService } from '../../core/services/alert.service';
import { ProductoListApi } from '../../core/models/api/producto-api';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { UiEmptyImageComponent } from '../../shared/components/ui-empty-image.component';

@Component({
    selector: 'app-admin-productos',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, UiLoadingComponent, UiEmptyImageComponent],
    template: `
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h2 class="text-2xl font-black text-gray-800">Productos</h2>
        <p class="text-sm text-gray-400 mt-1">{{ filtrados().length }} en el catálogo interno</p>
      </div>
      <a routerLink="/admin/productos/nuevo" class="px-5 py-2.5 rounded-2xl text-white text-sm font-bold text-center" style="background-color: var(--bg-main);">Nuevo producto</a>
    </div>

    <div class="mb-5">
      <input [ngModel]="busqueda()" (ngModelChange)="onSearch($event)" name="busqueda" placeholder="Buscar por nombre o SKU" class="w-full md:w-80 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading() && filtrados().length === 0" class="bg-white rounded-[2rem] border border-rose-100 p-12 text-center text-gray-400">
      No hay productos con ese criterio.
    </div>

    <div *ngIf="!loading()" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      <article *ngFor="let p of filtrados()" class="bg-white rounded-[1.75rem] border border-rose-100 shadow-sm overflow-hidden flex gap-4 p-3">
        <div class="w-24 h-24 rounded-2xl overflow-hidden bg-[#fff5f7] shrink-0">
          <img *ngIf="p.urlImagen" [src]="p.urlImagen" [alt]="p.nombre" class="w-full h-full object-cover" />
          <app-ui-empty-image *ngIf="!p.urlImagen"></app-ui-empty-image>
        </div>
        <div class="min-w-0 flex-1 flex flex-col">
          <p class="font-bold text-gray-800 leading-snug line-clamp-2">{{ p.nombre }}</p>
          <p class="text-xs text-gray-400 mt-0.5">{{ p.sku }}</p>
          <p class="text-sm font-black text-rose-500 mt-1">S/ {{ p.precioFinal.toFixed(2) }}</p>
          <div class="mt-auto pt-2 flex items-center justify-between">
            <span class="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full" [class.bg-emerald-50]="p.visibleCatalogo" [class.text-emerald-600]="p.visibleCatalogo" [class.bg-gray-100]="!p.visibleCatalogo" [class.text-gray-500]="!p.visibleCatalogo">
              {{ p.visibleCatalogo ? 'Visible' : 'Oculto' }}
            </span>
            <div class="flex gap-2 text-xs font-bold">
              <a [routerLink]="['/admin/productos', p.uuid]" class="text-rose-500">Editar</a>
              <button *ngIf="confirmUuid() !== p.uuid" type="button" (click)="confirmUuid.set(p.uuid)" class="text-gray-400">Quitar</button>
              <button *ngIf="confirmUuid() === p.uuid" type="button" (click)="eliminar(p)" class="text-rose-600">¿Seguro?</button>
            </div>
          </div>
        </div>
      </article>
    </div>
    `
})
export class AdminProductosComponent implements OnInit {
    private productoService = inject(ProductoService);
    private alerts = inject(AlertService);
    productos = signal<ProductoListApi[]>([]);
    filtrados = signal<ProductoListApi[]>([]);
    busqueda = signal('');
    confirmUuid = signal<string | null>(null);
    loading = signal(true);

    ngOnInit(): void {
        this.reload();
    }

    onSearch(value: string) {
        this.busqueda.set(value);
        this.filtrar();
    }

    reload() {
        this.loading.set(true);
        this.productoService.listProductos({ pageNumber: 1, pageSize: 100, incluirOcultos: true }).subscribe({
            next: (page) => {
                this.productos.set(page.data ?? []);
                this.filtrar();
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudieron cargar los productos.');
            }
        });
    }

    filtrar() {
        const q = this.busqueda().trim().toLowerCase();
        const list = this.productos();
        this.filtrados.set(
            q ? list.filter((p) => p.nombre.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)) : list
        );
    }

    eliminar(p: ProductoListApi) {
        this.productoService.deleteProducto(p.uuid).subscribe({
            next: (ok) => {
                this.confirmUuid.set(null);
                if (ok) {
                    this.alerts.ok('Producto quitado');
                    this.reload();
                } else {
                    this.alerts.error('No se pudo quitar');
                }
            }
        });
    }
}
